import React, { useState, useRef, useEffect } from "react";
import HighchartsReact from "highcharts-react-official";
import Highcharts from "highcharts";
import { getReadableTimeOnly, getReadableTime } from "../../utils/converter";
import Cookie from "js-cookie";
import "./graph_new.scss";
import { fontSize, fontWeight } from "@mui/system";

const Graph_new = ({
  patientID,
  fhrData,
  fhr2Data,
  mhrData,
  ucData,
  fourDataPoint,
  isNotificationComing,
  fetalMarker,
  automatedFetalMarker,
  spo2Data,
  pulseData,
  accountType,
  startTime,
  viewMode = "grid",
  isCritical = false,
}) => {
  const [theme, setTheme] = useState();
  const [allowScroll, setAllowScroll] = useState(false);

  const chartRef1 = useRef(null);
  const chartRef2 = useRef(null);
  const cookieData = Cookie.get("theme");
  const userInteractingRef = useRef(false);
  const lastAutoFollowRef = useRef(true);
  const lastUserXMaxRef = useRef(null);
  const frozenExtremesRef = useRef(null);
  // After the user scrolls away from live and then leaves the chart alone for
  // this long, it switches back to following live data.
  const RESUME_LIVE_AFTER_MS = 10000;
  const resumeLiveTimerRef = useRef(null);
  const liveWindowRef = useRef({ xMin: undefined, xMax: undefined });
  const isSingleView = viewMode === "single";

  // A chart interaction (scroll/drag) freezes the x-axis window via these
  // refs — they must release when switching to a different patient,
  // otherwise the new patient's chart stays stuck on the previous
  // patient's frozen window instead of following their own live data.
  useEffect(() => {
    userInteractingRef.current = false;
    frozenExtremesRef.current = null;
    clearTimeout(resumeLiveTimerRef.current);
  }, [patientID]);

  useEffect(() => () => clearTimeout(resumeLiveTimerRef.current), []);

  const shouldHideBand = isCritical && viewMode === "grid";

  const getNormalRangeBand = () => {
    if (shouldHideBand) return [];

    return [
      {
        id: "normal",
        color:
          theme === "light-theme"
            ? "#BAC7B333"
            : "rgba(255,255,255,0.1)",
        from: 110,
        to: 160,
        zIndex: -1,
      },
    ];
  };

  const FONT = {
    legend: isSingleView ? 12 : 8,
    xAxis: isSingleView ? 12 : 6,
    yAxis: isSingleView ? 12 : 6,
  };


  const Y_AXIS = {
    tickInterval: isSingleView ? 30 : 30,
    minorTickInterval: isSingleView ? 15 : 15,
  };

  // Single view: FHR axis 30-240 (major gridlines every 30), UC axis 0-100
  // (major every 25). Grid view keeps its compact 60-210 range.
  const Y_RANGE = isSingleView
    ? { min: 30, max: 240 }
    : { min: 60, max: 210 };
  // Fetal-movement markers ride along the top edge of the FHR chart.
  const MARKER_Y = isSingleView ? 236 : 205;
  const toMarkerRow = (markers) =>
    isSingleView ? (markers || []).map((m) => [m[0], MARKER_Y]) : markers || [];

  const Y_AXIS_UC = {
    tickInterval: isSingleView ? 25 : 30,
    minorTickInterval: isSingleView ? 12.5 : 15,
  };
  const FMM_MARKER_SIZE = isSingleView ? 6 : 3;
  const AMM_MARKER_SIZE = isSingleView ? 5 : 2.5;

  const syncExtremes = (e, source) => {
    if (!e.trigger || e.trigger === "sync") return;

    const isUserGesture =
      e.trigger === "pan" ||
      e.trigger === "navigator" ||
      e.trigger === "zoom" ||
      e.trigger === "touchpan";

    if (isUserGesture && isSingleView) {
      userInteractingRef.current = true;

      frozenExtremesRef.current = {
        min: e.min,
        max: e.max,
      };

      const liveEdge =
        fhrData.length > 0 ? fhrData[fhrData.length - 1][0] + gapSecs : null;

      clearTimeout(resumeLiveTimerRef.current);
      // Resume auto-follow if the user reaches live...
      if (liveEdge && Math.abs(liveEdge - e.max) < gapSecs) {
        userInteractingRef.current = false;
        frozenExtremesRef.current = null;
      } else {
        // ...or after they have stayed somewhere else for a while
        resumeLiveTimerRef.current = setTimeout(() => {
          userInteractingRef.current = false;
          frozenExtremesRef.current = null;
          const { xMin: liveMin, xMax: liveMax } = liveWindowRef.current;
          if (liveMin === undefined || liveMax === undefined) return;
          [chartRef1, chartRef2].forEach((ref) =>
            ref.current?.chart?.xAxis?.[0]?.setExtremes(liveMin, liveMax, true, false, {
              trigger: "sync",
            }),
          );
        }, RESUME_LIVE_AFTER_MS);
      }
    }

    const { min, max } = e;

    // Sync from top → bottom
    if (source === "top" && chartRef2.current?.chart?.xAxis?.[0]) {
      chartRef2.current.chart.xAxis[0].setExtremes(min, max, true, false, {
        trigger: "sync",
      });
    }

    // Sync from bottom → top
    if (source === "bottom" && chartRef1.current?.chart?.xAxis?.[0]) {
      chartRef1.current.chart.xAxis[0].setExtremes(min, max, true, false, {
        trigger: "sync",
      });
    }
  };

  useEffect(() => {
    if (cookieData) {
      setTheme(cookieData);
    }
  }, [cookieData]);

  // let dataStartTime = startTime
  //   ? String(startTime).length === 13
  //     ? Math.floor(startTime / 1000)
  //     : Number(startTime)
  //   : undefined;

  const dataStartTime = React.useMemo(() => {
    if (!startTime) return undefined;
    return String(startTime).length === 13
      ? Math.floor(startTime / 1000)
      : Number(startTime);
  }, [startTime]);

  // Define these OUTSIDE useMemo so they're accessible everywhere
  const WINDOW_MINUTES = isSingleView ? 15 : 10;
  const GAP_MINUTES = 1; // Keep a 1-minute buffer before fully auto-scrolling
  const windowSecs = WINDOW_MINUTES * 60;
  const gapSecs = GAP_MINUTES * 60;

  const { xMin, xMax, dataMin, dataMax } = React.useMemo(() => {
    if (fhrData.length === 0) {
      return {
        xMin: undefined,
        xMax: undefined,
        dataMin: undefined,
        dataMax: undefined,
      };
    }

    const firstPoint = fhrData[0]?.[0];

    const lastPoint = fhrData[fhrData.length - 1][0];
    const totalRange = lastPoint - firstPoint;

    const dataMin = firstPoint;

    const dataMax = Math.min(lastPoint + gapSecs, lastPoint + windowSecs);

    let xMin, xMax;
    if (totalRange < windowSecs) {
      xMin = firstPoint;
      xMax = firstPoint + windowSecs;
    } else {
      xMax = lastPoint + gapSecs;
      xMin = xMax - windowSecs;
    }

    return { xMin, xMax, dataMin, dataMax };
  }, [fhrData, windowSecs, gapSecs]);

  liveWindowRef.current = { xMin, xMax };

  useEffect(() => {
    if (!chartRef1.current?.chart || !chartRef2.current?.chart) return;

    // 🧊 USER MODE → lock window
    if (userInteractingRef.current && frozenExtremesRef.current) {
      const { min, max } = frozenExtremesRef.current;
      chartRef1.current.chart.xAxis[0].setExtremes(min, max, false, false);
      chartRef2.current.chart.xAxis[0].setExtremes(min, max, false, false);
      return;
    }

    // 🤖 AUTO MODE → follow live
    if (xMin === undefined || xMax === undefined) return;


    chartRef1.current.chart.xAxis[0].setExtremes(xMin, xMax, false, false);
    chartRef2.current.chart.xAxis[0].setExtremes(xMin, xMax, false, false);
  }, [xMin, xMax]);

  useEffect(() => {
    if (!chartRef1.current?.chart) return;
    if (userInteractingRef.current) return;

    lastUserXMaxRef.current = null;
  }, [xMax]);


  useEffect(() => {
    const chartContainer = document.querySelector(".graph-charts-container");

    if (!chartContainer) return;

    const resizeObserver = new ResizeObserver((entries) => {
      for (let entry of entries) {
        // Reflow both charts when container resizes
        if (chartRef1.current?.chart) {
          chartRef1.current.chart.reflow();
        }
        if (chartRef2.current?.chart) {
          chartRef2.current.chart.reflow();
        }
      }
    });

    resizeObserver.observe(chartContainer);

    return () => {
      resizeObserver.disconnect();
    };
  }, []);

  useEffect(() => {
    if (fhrData.length === 0 || !dataMin) return;

    const lastTimestamp = fhrData[fhrData.length - 1][0];
    const durationInSeconds = lastTimestamp - dataMin;

    const SLIDE_AFTER_SECS = windowSecs;
    setAllowScroll(durationInSeconds > SLIDE_AFTER_SECS);
  }, [fhrData, dataMin]);


  useEffect(() => {
    if (!chartRef1.current?.chart) return;

    const chart = chartRef1.current.chart;

    // Remove existing
    chart.yAxis[0].removePlotBand("normal");

    // ADD only when allowed
    if (!(isCritical && viewMode === "grid")) {
      chart.yAxis[0].addPlotBand({
        id: "normal",
        color:
          theme === "light-theme"
            ? "#BAC7B333"
            : "#BAC7B333",
        from: 120,
        to: 160,
        zIndex: -1,
      });
    }
  }, [isCritical, theme, viewMode]);

  //   useEffect(() => {
  //     try {
  //       if (!chartRef1.current?.chart || fhrData.length === 0) return;

  // const firstTimestamp = dataStartTime;
  // const lastTimestamp = fhrData[fhrData.length - 1][0] || dataStartTime;
  //       const dataRangeSecs = lastTimestamp - firstTimestamp;

  //       let newMin = dataStartTime;

  //       let newMax;

  //       // Show 15min window if data is less than that, otherwise show all data
  //       if (dataRangeSecs < windowSecs) {
  //         newMax = firstTimestamp + windowSecs;
  //       } else {
  //         newMax = lastTimestamp; // ✅ Shows full historical range
  //       }

  //       // Update both charts
  //       if (chartRef1.current?.chart?.xAxis?.[0]) {
  //         chartRef1.current.chart.xAxis[0].setExtremes(
  //           newMin,
  //           newMax,
  //           true,
  //           false
  //         );
  //       }
  //       if (chartRef2.current?.chart?.xAxis?.[0]) {
  //         chartRef2.current.chart.xAxis[0].setExtremes(
  //           newMin,
  //           newMax,
  //           true,
  //           false
  //         );
  //       }
  //     } catch (e) {
  //       console.warn("Failed to set chart extremes", e);
  //     }
  //   }, [fhrData, windowSecs]); // ✅ Added windowSecs dependency

  const options1 = {
    chart: {
      // backgroundColor:
      //   theme === "light-theme" ? "rgba(255, 255, 255, 1)" : "#0E0E0E",
      animation: false,
      width: null,
      height: null,
      spacing: [2.7, 2.2, 2.4, 2.5],
      reflow: false,
      panning: {
        enabled: isSingleView,
        type: "x",
      },
      marginLeft: isSingleView ? 40 : 30,
      marginRight: 5,
      spacingTop: window.innerHeight >= 2000 ? 36 : 6,
      spacingBottom: isSingleView
        ? window.innerHeight >= 2000
          ? 12
          : 6
        : window.innerHeight >= 2000
          ? 6
          : 2,
      spacingLeft: 7,
      spacingRight: 6,
      style: {
        width: "100%",
        height: "100%",
      },
    },
    plotOptions: {
      series: {
        turboThreshold: 0,
        // No initial reveal animation: it clips the series with a temporary
        // rect sized for the chart's FIRST layout and only releases it after a
        // quiet second. This chart updates every second, so after the card
        // grew from grid to single size the line stayed cut off at the old
        // width (and looked fine again once the window shrank).
        animation: false,
        gapSize: 10,
        gapUnit: "value",
      },
    },
    credits: {
      enabled: false,
    },
    title: {
      text: "",
    },
    scrollbar: {
      enabled: allowScroll, // ✅ Enable scrollbar only after 15min
    },
    tooltip: {
      formatter: function () {
        return (
          "<b>" +
          this.series.name +
          "</b><br/>" +
          getReadableTime(parseInt(this.key)) +
          "<br/>" +
          this.y
        );
      },
    },
    xAxis: {
      type: "datetime",
      dateTimeLabelFormats: {
        millisecond: "%H:%M:%S.%L",
        second: "%H:%M:%S",
        minute: "%H:%M",
        hour: "%H:%M",
        day: "%e %b",
        week: "%e %b",
        month: "%b '%y",
        year: "%Y",
      },
      // labels: {
      //   align: "center",
      //   x: 0,
      //   style: {
      //     color:
      //       theme === "light-theme"
      //         ? "#4A4A4A"
      //         : `rgba(255,255,255,0.7)`,
      //       fontWeight: "600",
      //   },
      //   formatter: function () {
      //     return getReadableTimeOnly(parseInt(this.value));
      //   },
      // },

      labels: {
        enabled: false,
      },

      min: undefined,
      max: undefined,
      softMax: dataMax,
      minRange: 300,
      maxPadding: 0.2,
      alignTicks: true,
      startOnTick: false,
      endOnTick: false,
      ignoreHiddenSeries: true,
      scrollbar: {
        enabled: allowScroll,
      },
      overscroll: gapSecs * 1000,
      events: {
        setExtremes: function (e) {
          syncExtremes(e, "top");
        },
      },
      lineColor:
        theme === "light-theme"
          ? "rgba(183, 183, 183, 1)"
          : `rgba(255,255,255,0.3)`,
      lineWidth: 0,
      gridLineWidth: 0,
      tickPositioner: function () {
        if (!dataMin) return this.tickPositions;
        let positions = [];
        const interval = 180;
        // Anchor to a whole clock minute (not the first point's seconds);
        // the label formatter then names the minute that just ended.
        const origin = Math.ceil(dataMin / 60) * 60;
        let k = Math.ceil((this.min - origin) / interval);
        if (k < 0) k = 0;

        let tick = origin + k * interval;
        while (tick <= this.max) {
          positions.push(tick);
          tick += interval;
        }
        return positions;
      },
      tickInterval: 180,
      minorTickInterval: 30,

      // gridLineWidth: 1,
      // gridZIndex: -1,
      tickWidth: 0,
      gridLineWidth: isSingleView ? 0.8 : 0.4,

      gridLineColor:
        theme === "light-theme"
          ? "rgba(127, 127, 127, 1)"
          : "rgba(255, 255, 255, 0.4)",
      tickColor:
        theme === "light-theme"
          ? "rgba(127, 127, 127, 1)"
          : `rgba(255,255,255,0.3)`,
      minorGridLineColor:
        theme === "light-theme"
          ? "rgba(127, 127, 127, 1)"
          : `rgba(255,255,255,0.3)`,
      minorGridLineWidth: isSingleView ? 0.6 : 0.3,
      title: {
        text: "",
        style: {
          color:
            theme === "light-theme"
              ? "rgba(183, 183, 183, 1)"
              : `rgba(255,255,255,0.7)`,
        },
      },
    },
    yAxis: {
      title: {
        text: "",
        style: {
          color:
            theme === "light-theme"
              ? "rgba(183, 183, 183, 1)"
              : `rgba(255,255,255,0.7)`,
        },
      },
      labels: {
        x:-8,
        y: isSingleView ? 0 : 2,
        style: {
          fontSize: `${FONT.yAxis}px`,
          color: theme === "light-theme" ? "#4A4A4A" : `rgba(255,255,255,0.7)`,
          fontWeight: "500",
        },
      },
      plotBands: getNormalRangeBand(),
      // plotBands: [
      //   {
      //     color:
      //       theme === "light-theme"
      //         ? "rgba(249, 255, 246, 1)"
      //         : `rgba(255,255,255,0.1)`,
      //     from: 120,
      //     to: 160,
      //     zIndex: -1,
      //   },
      // ],
      lineWidth: 0.3,
      lineColor:
        theme === "light-theme"
          ? "rgba(183, 183, 183, 1)"
          : `rgba(255,255,255,0.7)`,
      tickInterval: Y_AXIS.tickInterval,
      minorTickInterval: Y_AXIS.minorTickInterval,
      min: Y_RANGE.min,
      max: Y_RANGE.max,
      scrollbar: {
        enabled: false,
      },
      minorGridLineWidth: 0.3,
      startOnTick: false,
      endOnTick: false,
      gridLineColor:
        theme === "light-theme"
          ? "rgba(177, 176, 176, 1)"
          : "rgba(255,255,255,0.3)",
      tickColor:
        theme === "light-theme"
          ? "rgba(131, 130, 130, 1)"
          : `rgba(253, 8, 8, 0.1)`,
      minorGridLineColor:
        theme === "light-theme"
          ? "rgba(177, 176, 176, 1)"
          : `rgba(255,255,255,0.3)`,
    },

    legend: {
      enabled: isSingleView,
      layout: "horizontal",
      align: "center",
      verticalAlign: "top",
      itemDistance: isSingleView ? 16 : 3,
      y: isSingleView ? -12 : -8,
      margin: 0,
      itemMarginBottom: 0,
      itemStyle: {
        fontSize: `${FONT.legend}px`,
        color:
          theme === "light-theme"
            ? "rgba(0,0,0, 0.7)"
            : `rgba(255,255,255,0.7)`,
        fontWeight: "300",
      },
    },
    // legend: {
    //   enabled: false,
    // },
    series: [
      {
        name: "FHR",
        data: fhrData,

        connectNulls: false,

        marker: {
          symbol: "circle",
        },
        lineWidth: 1.5,
        color: theme === "light-theme" ? "#1C1F89" : "#0393D9",
      },
      {
        name: "MHR",
        data: mhrData,

        connectNulls: false,

        marker: {
          symbol: "circle",
        },
        lineWidth: 1.5,
        color: "#E13D92",
      },
      {
        name: "FHR2",
        data: fhr2Data,

        connectNulls: false,

        marker: {
          symbol: "circle",
        },
        lineWidth: 1.5,
        color: theme === "light-theme" ? "#841717" : "#FF6905",
      },
      {
        name: "FMM",
        data: toMarkerRow(fetalMarker),
        type: "scatter",
        marker: {
          radius: FMM_MARKER_SIZE,
          symbol: "circle",
          fillColor: "rgb(239, 55, 151)",
        },
        color: "rgb(239, 55, 151)",
        zIndex: 50,
      },
      {
        name: "AMM",
        data: toMarkerRow(automatedFetalMarker),
        type: "scatter",
        marker: {
          radius: AMM_MARKER_SIZE,
          symbol: "circle",
          fillColor: "white",
          lineWidth: 1.5,
          lineColor: "rgb(239, 55, 151)",
        },
        color: "rgb(239, 55, 151)",
        zIndex: 5,
      },
    ],
  };

  const options2 = {
    chart: {
      // backgroundColor:
      //   theme === "light-theme" ? "rgba(255, 255, 255, 1)" : "#0E0E0E",
      animation: false,
      height: null,
      width: null,
      spacingTop: isSingleView ? (window.innerHeight >= 2000 ? 36 : 6) : 0,
      spacingBottom: isSingleView ? 28 : 16,
      spacing: [3.2, 2.2, 2.5, 2.4],
      reflow: false,
      panning: {
        enabled: isSingleView,
        type: "x",
      },
      marginLeft: isSingleView ? 40 : 30,
      marginRight: 5,
      style: {
        width: "100%",
        height: "100%",
      },
    },
    plotOptions: {
      series: {
        turboThreshold: 0,
        // No initial reveal animation: it clips the series with a temporary
        // rect sized for the chart's FIRST layout and only releases it after a
        // quiet second. This chart updates every second, so after the card
        // grew from grid to single size the line stayed cut off at the old
        // width (and looked fine again once the window shrank).
        animation: false,
        gapSize: 10,
        gapUnit: "value",
      },
    },
    credits: {
      enabled: false,
    },
    title: {
      text: "",
    },
    tooltip: {
      formatter: function () {
        return (
          "<b>" +
          this.series.name +
          "</b><br/>" +
          getReadableTime(parseInt(this.key)) +
          "<br/>" +
          this.y
        );
      },
    },
    xAxis: {
      type: "datetime",
      dateTimeLabelFormats: {
        millisecond: "%H:%M:%S.%L",
        second: "%H:%M:%S",
        minute: "%H:%M",
        hour: "%H:%M",
        day: "%e %b",
        week: "%e %b",
        month: "%b '%y",
        year: "%Y",
      },
      labels: {
        y: isSingleView ? 24 : 10,
        style: {
          fontSize: `${FONT.xAxis}px`,
          color: theme === "light-theme" ? "#4A4A4A" : `rgba(255,255,255,0.7)`,
          fontWeight: "500",
        },
        formatter: function () {
          // A line labelled HH:MM marks the END of that minute, so every
          // point whose tooltip reads HH:MM sits to the left of its line.
          return getReadableTimeOnly(parseInt(this.value) - 60);
        },
      },
      min: undefined,
      max: undefined,
      minRange: 300,
      maxPadding: 0.2,
      softMax: dataMax,
      startOnTick: false,
      ignoreHiddenSeries: true,
      endOnTick: false,
      scrollbar: {
        enabled: allowScroll,
      },
      overscroll: gapSecs * 1000,
      events: {
        setExtremes: function (e) {
          syncExtremes(e, "bottom");
        },
      },
      lineColor:
        theme === "light-theme"
          ? "rgba(183, 183, 183, 1)"
          : `rgba(255,255,255,0.7)`,
      lineWidth: 0.3,
      tickPositioner: function () {
        if (!dataMin) return this.tickPositions;
        let positions = [];
        const interval = 180;
        // Anchor to a whole clock minute (not the first point's seconds);
        // the label formatter then names the minute that just ended.
        const origin = Math.ceil(dataMin / 60) * 60;
        let k = Math.ceil((this.min - origin) / interval);
        if (k < 0) k = 0;

        let tick = origin + k * interval;
        while (tick <= this.max) {
          positions.push(tick);
          tick += interval;
        }
        return positions;
      },
      tickInterval: 180,
      minorTickInterval: 30,
      tickLength: 0,
      minorTickLength: 0,
      gridLineColor:
        theme === "light-theme"
          ? "rgba(127, 127, 127, 1)"
          : `rgba(255,255,255,0.7)`,
      gridLineWidth: isSingleView ? 0.8 : 0.4,
      tickColor:
        theme === "light-theme"
          ? "rgba(131, 130, 130, 1)"
          : `rgba(255,255,255,0.1)`,
      minorGridLineColor:
        theme === "light-theme"
          ? "rgba(127, 127, 127, 1)"
          : `rgba(255,255,255,0.3)`,
      minorGridLineWidth: isSingleView ? 0.6 : 0.3,
      title: {
        text: "",
        style: {
          color:
            theme === "light-theme"
              ? "rgba(127, 127, 127, 1)"
              : `rgba(255,255,255,0.3)`,
        },
      },
    },
    yAxis: {
      title: {
        text: "",
        style: {
          color:
            theme === "light-theme"
              ? "rgba(183, 183, 183, 1)"
              : `rgba(255,255,255,0.7)`,
        },
      },
      labels: {
        x:-8,
        y: isSingleView ? 7 : 4,
        style: {
          fontSize: `${FONT.yAxis}px`,
          color: theme === "light-theme" ? "#4A4A4A" : `rgba(255,255,255,0.7)`,
          fontWeight: "500",
        },
      },
      plotBands: getNormalRangeBand(),
      // plotBands: [
      //   {
      //     color: `rgba(255,255,255,0.1)`,
      //     from: 120,
      //     to: 160,
      //     zIndex: -1,
      //   },
      // ],
      lineWidth: 0.2,
      lineColor:
        theme === "light-theme"
          ? "rgba(183, 183, 183, 1)"
          : `rgba(255,255,255,0.7)`,
      tickInterval: Y_AXIS_UC.tickInterval,
      minorTickInterval: Y_AXIS_UC.minorTickInterval,
      min: 0,
      max: isSingleView ? 100 : 90,
      startOnTick: false,
      endOnTick: false,
      minorGridLineWidth: 0.3,
      gridLineColor:
        theme === "light-theme"
          ? "rgba(177, 176, 176, 1)"
          : "rgba(255,255,255,0.3)",
      tickColor:
        theme === "light-theme"
          ? "rgba(127, 127, 127, 1)"
          : `rgba(255,255,255,0.3)`,
      minorGridLineColor:
        theme === "light-theme"
          ? "rgba(177, 176, 176, 1)"
          : `rgba(255,255,255,0.3)`,
    },
    // legend: {
    //   itemStyle: {
    //     color:
    //       theme === "light-theme"
    //         ? "rgba(0,0,0, 0.7)"
    //         : `rgba(255,255,255,0.7)`,
    //   },
    // },
    legend: {
      enabled: isSingleView,
      layout: "horizontal",
      align: "center",
      verticalAlign: "top",
      itemDistance: isSingleView ? 16 : 3,
      y: isSingleView ? -12 : -8,
      margin: 0,
      itemMarginBottom: 0,
      itemStyle: {
        fontSize: `${FONT.legend}px`,
        color:
          theme === "light-theme"
            ? "rgba(0,0,0, 0.7)"
            : `rgba(255,255,255,0.7)`,
        fontWeight: "300",
      },
    },
    series: [
      {
        name: "UC",
        data: ucData,
        connectNulls: false,
        lineWidth: 1.5,
        marker: {
          symbol: "circle",
        },
        color: "#F59E0B",
      },
      {
        name: "SpO₂",
        data: spo2Data,
        connectNulls: false,
        lineWidth: 1.5,
        marker: {
          symbol: "circle",
        },
        color: "#00F5FF",
      },
    ],
  };

  const emptyChartOptions = {
    chart: {
      backgroundColor:
        theme === "light-theme" ? "rgba(255, 255, 255, 1)" : "#0E0E0E",
      animation: false,
      height: null,
      reflow: false,
    },
    credits: {
      enabled: false,
    },
    title: {
      text: "",
    },
    tooltip: {
      formatter: function () {
        return (
          "<b>" +
          this.series.name +
          "</b><br/>" +
          getReadableTime(parseInt(this.key)) +
          "<br/>" +
          this.y
        );
      },
    },
    xAxis: {
      type: "datetime",
      labels: {
        style: {
          color:
            theme === "light-theme"
              ? "rgba(127, 127, 127, 1)"
              : `rgba(255,255,255,0.7)`,
        },
      },
      min: dataStartTime,
      max: dataStartTime ? dataStartTime + 21 * 60 : undefined,
      scrollbar: {
        enabled: false,
      },
    },
    yAxis: {
      min: 0,
      max: 100,
    },
    // legend: {
    //   itemStyle: {
    //     color:
    //       theme === "light-theme"
    //         ? "rgba(0,0,0, 0.7)"
    //         : `rgba(255,255,255,0.7)`,
    //   },
    // },
    series: [
      {
        data: [],
      },
      {
        data: [],
      },
    ],
  };

  return (
    <>
      {accountType && accountType === "Navam" ? (
        <div>
          <HighchartsReact
            highcharts={Highcharts}
            options={emptyChartOptions}
            constructorType={"chart"}
            allowChartUpdate={true}
            className={
              theme === "light-theme" ? "highcharts-light" : "highcharts-dark"
            }
          />
          <HighchartsReact
            highcharts={Highcharts}
            options={emptyChartOptions}
            constructorType={"chart"}
            allowChartUpdate={true}
            className={
              theme === "light-theme" ? "highcharts-light" : "highcharts-dark"
            }
          />
        </div>
      ) : (
        <div
          className="graph-charts-container"
          data-allow-scroll={allowScroll} // ✅ CSS will use this
        >
          <div className="graph-chart-top">
            <HighchartsReact
              ref={chartRef1}
              highcharts={Highcharts}
              options={options1}
              constructorType={"chart"}
              allowChartUpdate={true}
              className={
                theme === "light-theme" ? "highcharts-light" : "highcharts-dark"
              }
            />
          </div>

          <div className="graph-chart-bottom">
            <HighchartsReact
              ref={chartRef2}
              highcharts={Highcharts}
              options={options2}
              constructorType={"chart"}
              allowChartUpdate={true}
              className={
                theme === "light-theme" ? "highcharts-light" : "highcharts-dark"
              }
            />
          </div>
        </div>
      )}
    </>
  );
};

export default Graph_new;
